import { useToast } from "@chakra-ui/react";
import { useContext } from "react";
import { useNavigate } from "react-router";

import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { BannerAlert, Text } from "@equidefi/ui";

import { useLockHistory } from "@/hooks/useLockHistory";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import { useSaveVaultAdditionalInfo } from "@equidefi/portals/hooks/useVault";
import {
  associationsOptions,
  employmentStatusOptions,
  investmentObjectiveOptions,
  maritalStatusOptions,
} from "@equidefi/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";

const additionalInfoSchema = z.object({
  marital_status: z.string(),
  employment_status: z.string(),
  associations: z.array(z.string()).optional(),
  investment_objective: z.string().refine((val) => val === "Speculation", {
    message: 'You must select "Speculation" as your investment objective',
  }),
});

type AdditionalInfoFormData = z.infer<typeof additionalInfoSchema>;

export const AdditionalInfoForm = () => {
  const { investment, analytics, offering } = useWorkflowContext();
  const { vault } = useContext(VaultContext);
  const navigate = useNavigate();
  useDocumentTitle(["Accreditation", offering?.name, investment?.issuer?.name]);
  useLockHistory();

  const saveVaultInfo = useSaveVaultAdditionalInfo(investment?.id as string);

  const toast = useToast();

  const navigateToVault = () => {
    navigate("/vault/dashboard", {
      state: {
        completed: true,
        isPaid: Number(investment?.payments?.filter((p) => p.status === "succeeded")?.length) > 0,
        investment_id: investment?.id,
      },
    });
  };

  const onSubmit = async (values: AdditionalInfoFormData) => {
    try {
      await saveVaultInfo.mutateAsync({
        marital_status: {
          label: "Marital Status",
          value: values.marital_status,
        },
        employment_status: {
          label: "Employment Status",
          value: values.employment_status,
        },
        associations: {
          label: "Associations",
          value: values.associations,
        },
        investment_objective: {
          label: "Investment Objectives",
          value: values.investment_objective,
        },
      });
      analytics.track("complete");
      navigateToVault();
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: "Failed to submit",
      });
    }
  };

  const form = useForm<AdditionalInfoFormData>({
    defaultValues: {
      marital_status: vault?.additional_info?.marital_status?.value,
      employment_status: vault?.additional_info?.employment_status?.value,
      associations: vault?.additional_info?.associations?.value,
      investment_objective: vault?.additional_info?.investment_objective?.value,
    },
    resolver: zodResolver(additionalInfoSchema),
  });

  return (
    <WorkflowContainer
      title="Additional Info"
      onContinue={form.handleSubmit(onSubmit)}
      buttonLabel="Finish Investment"
      buttonProps={{
        isDisabled: !form.formState.isValid,
      }}>
      <WorkflowForm onSubmit={form.handleSubmit(onSubmit)}>
        <SmartFormProvider form={form}>
          {({ RadioGroup, CheckboxGroup }) => (
            <>
              <Text fontWeight="bold" textStyle="h2" mb="2" mt={5}>
                Marital status
              </Text>
              <RadioGroup
                name="marital_status"
                direction="wrap"
                elementWidth="32%"
                spacing={2.5}
                mb="4"
                options={maritalStatusOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
              />

              <Text fontWeight="bold" textStyle="h2" mb="2">
                Employment status
              </Text>
              <RadioGroup
                name="employment_status"
                direction="wrap"
                elementWidth="32%"
                spacing={2.5}
                mb="4"
                options={employmentStatusOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
              />

              <Text fontWeight="bold" textStyle="h2" mb="0" mt={10}>
                Associations
              </Text>
              <Text mb="2.5">Select all that apply</Text>
              <CheckboxGroup name="associations" options={associationsOptions} mb="3" />

              <Text fontWeight="bold" textStyle="h2" mb={3} mt={10}>
                Investment Objective
              </Text>
              <Text mb={2}>Select one of the following</Text>
              <RadioGroup
                name="investment_objective"
                direction="vertical"
                spacing={2.5}
                mb="4"
                options={investmentObjectiveOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
              />

              <BannerAlert
                mb={4}
                mt={6}
                status="warning"
                description="This investment is only available to investors whose objective is Speculation."
              />
            </>
          )}
        </SmartFormProvider>
      </WorkflowForm>
    </WorkflowContainer>
  );
};
